import React from 'react';
import PropTypes from 'prop-types';
import { FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';

/**
 * "Layout" card. Controls how items in the header bar are arranged.
 * All copy via the i18n `t` prop with English fallbacks.
 */
const LayoutCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb = '') => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    const menuStyles = [
        { value: 'right', label: tr('cfg_nav_layout_menu_style_right', 'Menu on the right of the header') },
        { value: 'below', label: tr('cfg_nav_layout_menu_style_below', 'Menu below the header')           },
        { value: 'left',  label: tr('cfg_nav_layout_menu_style_left',  'Menu on the left of the header')  },
    ];
    const actionPositions = [
        { value: 'inline',         label: tr('cfg_nav_layout_action_inline',  'Inline with the header') },
        { value: 'fixed-lowered',  label: tr('cfg_nav_layout_action_lowered', 'Fixed and lowered down') },
        { value: 'floating',       label: tr('cfg_nav_layout_action_float',   'Floating bottom-right')  },
    ];

    return (
        <ConfigCard
            title={tr('cfg_nav_layout_title', 'Layout')}
            description={tr('cfg_nav_layout_desc', 'How the items in the header bar are arranged.')}
            theme={theme}
        >
            <TextField
                select
                size="small"
                label={tr('cfg_nav_layout_menu_style', 'Menu style')}
                value={get('menuStyle', 'right') || 'right'}
                onChange={(e) => set('menuStyle')(e.target.value)}
                fullWidth
            >
                {menuStyles.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
            </TextField>

            <TextField
                select
                size="small"
                label={tr('cfg_nav_layout_action_position', 'Action buttons position')}
                value={get('actionButtonsPosition', 'inline') || 'inline'}
                onChange={(e) => set('actionButtonsPosition')(e.target.value)}
                fullWidth
            >
                {actionPositions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
            </TextField>

            <Stack spacing={0.5}>
                <FormControlLabel
                    control={
                        <Switch color="success"
                            size="small"
                            checked={!!get('centerFooterItemsHorizontally')}
                            onChange={(e) => set('centerFooterItemsHorizontally')(e.target.checked)}
                        />
                    }
                    label={<Typography variant="body2">{tr('cfg_nav_layout_center_footer', 'Center all footer items horizontally')}</Typography>}
                />
                <FormControlLabel
                    control={
                        <Switch color="success"
                            size="small"
                            checked={!!get('centerTopHeaderButtons')}
                            onChange={(e) => set('centerTopHeaderButtons')(e.target.checked)}
                        />
                    }
                    label={<Typography variant="body2">{tr('cfg_nav_layout_center_top_buttons', 'Center the top header buttons')}</Typography>}
                />
                <FormControlLabel
                    control={
                        <Switch color="success"
                            size="small"
                            checked={!!get('centerTopButtonInMiddle')}
                            onChange={(e) => set('centerTopButtonInMiddle')(e.target.checked)}
                        />
                    }
                    label={<Typography variant="body2">{tr('cfg_nav_layout_center_top_button', 'Center the top button in the middle of the header')}</Typography>}
                />
                <FormControlLabel
                    control={
                        <Switch color="success"
                            size="small"
                            checked={!!get('alignActionButtonsRight')}
                            onChange={(e) => set('alignActionButtonsRight')(e.target.checked)}
                        />
                    }
                    label={<Typography variant="body2">{tr('cfg_nav_layout_align_action_right', 'Align action buttons to the right of the header')}</Typography>}
                />
            </Stack>
        </ConfigCard>
    );
};

LayoutCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default LayoutCard;
